import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import FormDocument from "../components/FormDocument";
import SignaturePad from "../components/SignaturePad";

const FILL = { owner_auth: ["owner_work_place", "owner_civil_id"], sale_contract: ["buyer_civil_id", "buyer_work_phone", "buyer_work_place"] };
const ID_FIELDS = ["owner_civil_id", "buyer_civil_id"];
// ID arrives masked; the field starts empty so the customer can type a correction.
export const splitIds = (data = {}) => [
  Object.fromEntries(Object.entries(data).map(([k, v]) => [k, ID_FIELDS.includes(k) ? "" : v])),
  Object.fromEntries(ID_FIELDS.filter((k) => data[k]).map((k) => [k, data[k]])),
];
const SIGN_HERE_BILINGUAL = ["Sign here", "وقّع هنا"].join(" · "); // i18n-ignore (bilingual paper)

/** Tablet customer mode: only the form; leaving needs the staff password. */
export default function KioskFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useT();
  const [f, setF] = useState(null);
  const [showroom, setShowroom] = useState({});
  const [vals, setVals] = useState({});
  const [masked, setMasked] = useState({});
  const [sig, setSig] = useState(null);
  const [done, setDone] = useState(false);
  const [exit, setExit] = useState(false);
  const [pw, setPw] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/showroom-forms/${id}`, { params: { kiosk: 1 } });
      const [v, m] = splitIds(data.data);
      setF(data); setVals(v); setMasked(m);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => {
    sessionStorage.setItem("kiosk_lock", id);
    load();
    api.get("/settings").then(({ data }) => setShowroom(data || {})).catch(() => {});
    const trap = () => window.history.pushState(null, "", window.location.href);
    trap();
    window.addEventListener("popstate", trap);
    return () => window.removeEventListener("popstate", trap);
  }, [id, load]);

  const already = !!f?.signatures?.customer;
  const missing = f ? FILL[f.form_type].filter((k) => !(f.data || {})[k] || ID_FIELDS.includes(k)) : [];
  const editable = Object.fromEntries(missing.map((k) => [k, true]));
  const submit = async () => {
    try {
      const fill = Object.fromEntries(missing.filter((k) => vals[k]).map((k) => [k, vals[k]]));
      if (Object.keys(fill).length) await api.patch(`/showroom-forms/${id}`, { data: fill });
      await api.post(`/showroom-forms/${id}/sign`, { party: "customer", signature: sig });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unlock = async () => {
    try {
      await api.post("/showroom-forms/kiosk/verify", { password: pw });
      sessionStorage.removeItem("kiosk_lock");
      document.fullscreenElement && document.exitFullscreen?.().catch(() => {});
      nav(`/forms/sf/${id}`, { replace: true });
    } catch (e) { toast.error(formatApiError(e)); setPw(""); }
  };
  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: "var(--bg, #f4f3f1)" }} data-testid="kiosk-page">
      <div className="max-w-4xl mx-auto space-y-4">
        {f && !done && !already && <>
          <FormDocument form={f} showroom={showroom} editable={editable} values={vals} onChange={(k, v) => setVals({ ...vals, [k]: v })} placeholders={masked} hideSignatures />
          <div className="panel" style={{ padding: 16 }}>
            <SignaturePad label={SIGN_HERE_BILINGUAL} onChange={setSig} testid="kiosk-pad" />
            <button data-testid="kiosk-submit" className="btn-primary h-12 px-6 mt-3 w-full" disabled={!sig} onClick={submit}>{t("sf.submit_sign")}</button>
          </div>
        </>}
        {(done || already) && <div className="panel text-center text-xl" style={{ padding: 40 }} data-testid="kiosk-thanks">{t("sf.thanks")}<div dir="rtl" className="mt-2">شكراً لك. تم توقيع النموذج.</div></div>}
        <div className="flex justify-end">
          {!exit ? <button data-testid="kiosk-exit" className="h-11 px-4 rounded-lg border text-sm opacity-70" onClick={() => setExit(true)}>{t("sf.exit")}</button> : (
            <div className="flex gap-2">
              <input data-testid="kiosk-password" type="password" autoComplete="current-password" aria-label={t("sf.staff_pw")} placeholder={t("sf.staff_pw")} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-lg border px-3" />
              <button data-testid="kiosk-unlock" className="btn-primary h-11 px-4" onClick={unlock}>{t("sf.unlock")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
